export type PrimarySurface = 'home' | 'explore' | 'decode' | 'codex' | 'tribe';

export type QualityTier = 'auto' | 'low' | 'medium' | 'high';

export type MotionPreference = 'system' | 'reduce' | 'full';

export type CulturalReviewStatus =
  'draft' | 'needs_cultural_review' | 'reviewed' | 'approved' | 'retired';

export type ResourceCode = 'maize' | 'stone' | 'timber' | 'jade' | 'knowledge';

export interface PrimaryNavigationItem {
  id: PrimarySurface;
  label: string;
  href: string;
}

export interface ResourceDisplay {
  code: ResourceCode;
  label: string;
  amount: number;
}

export interface PresentationPreferences {
  qualityTier: QualityTier;
  motionPreference: MotionPreference;
  interfaceScale: 'default' | 'large';
  highContrast: boolean;
}

export interface JourneySafetyState {
  mode: 'stationary' | 'journey-recording' | 'journey-passive';
  message?: string;
}

export type BuildingType =
  | 'solar_shrine'
  | 'homes'
  | 'chinampa_farm'
  | 'stone_yard'
  | 'timber_workshop'
  | 'obsidian_workshop'
  | 'codex_house'
  | 'market'
  | 'dock'
  | 'garden';

export interface BuildingView {
  id: string;
  type: BuildingType;
  label: string;
  level: number;
  maxLevel?: number;
  /** Active server job target; initial foundations can target the already-stored level. */
  constructionTargetLevel?: number;
  upgradeDurationSeconds?: number;
  gridX: number;
  gridY: number;
  /** Effective, already-rotated ground footprint supplied by the game server. */
  footprintWidth?: number;
  footprintHeight?: number;
  orientation: number;
  variant: string;
  districtCode: string;
  branchCode?: string;
  visualTier: 0 | 1 | 2 | 3;
  availableBranches: readonly BuildingBranchView[];
  specializationLevel?: number;
  state: 'idle' | 'producing' | 'upgrade_available' | 'constructing';
  progress?: number;
  collectable?: boolean;
  upgradeCost: readonly ResourceDisplay[];
  benefit: string;
  /** Catalog preview only; actual active capabilities come from the settlement response. */
  plannedCapabilities?: readonly string[];
  plannedBranchCapabilities?: Readonly<Record<string, readonly string[]>>;
}

export interface SettlementDistrictView {
  code: string;
  displayName: string;
  unlockOrder: number;
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  unlocked: boolean;
  unlockedAt?: string;
  milestoneSource?: string;
  unlockRequirements: Readonly<Record<string, number>>;
}

export interface SettlementHistoryEventView {
  id: string;
  eventType: string;
  occurredAt: string;
  districtCode: string;
  buildingId?: string;
  buildingCode?: string;
  specializationCode?: string;
  skinCode?: string;
  milestoneSource: string;
}

export interface WorldCapabilityView {
  code: string;
  label: string;
  effectiveLevel: number;
  sourceBuildingId: string;
  sourceBuildingCode: string;
  specializationCode?: string;
  parameters: Readonly<Record<string, unknown>>;
}

export interface BuildingBranchView {
  code: string;
  label: string;
  description: string;
}

export interface ConstructionView {
  id: string;
  buildingId: string;
  completesAt?: string;
  progress: number;
  remainingLabel: string;
}

export interface MissionStepView {
  id: 'discover' | 'decode' | 'build';
  label: string;
  complete: boolean;
}

export interface MissionSummaryView {
  id: string;
  title: string;
  description: string;
  progress: number;
  target: number;
  steps: readonly MissionStepView[];
  rewardLabel: string;
}

export interface SettlementViewModel {
  settlementId: string;
  name: string;
  level: number;
  visualTier: 0 | 1 | 2 | 3;
  biome: string;
  resources: readonly ResourceDisplay[];
  buildings: readonly BuildingView[];
  construction: readonly ConstructionView[];
  districts: readonly SettlementDistrictView[];
  history: readonly SettlementHistoryEventView[];
  worldCapabilities: readonly WorldCapabilityView[];
  ambientPopulation: number;
  activeMission: MissionSummaryView;
}

export interface DiscoveryMarkerView {
  id: string;
  kind: 'glyph_fragment' | 'ruin' | 'cache' | 'celestial' | 'tribe' | 'seasonal';
  title: string;
  description: string;
  longitude: number;
  latitude: number;
  state: 'unknown' | 'nearby' | 'eligible' | 'queued' | 'claimed' | 'cooldown';
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  visualFamily: string;
  availableUntil?: string;
  worldNodeId?: string;
  nodeName?: string;
  nodeKind?:
    | 'glyph_archive'
    | 'civic_nexus'
    | 'verdant_waypoint'
    | 'water_gate'
    | 'celestial_observatory'
    | 'market_exchange';
  nodeTier?: 1 | 2 | 3 | 4 | 5;
  activityCode?: string;
  activityTitle?: string;
  activityObjective?: string;
  activityMechanic?: string;
  routeRole?: string;
  tierEffect?: string;
  decodeModifiers?: readonly string[];
  rewardPreview?: readonly string[];
  attunementLevel?: 0 | 1 | 2 | 3 | 4 | 5;
  weaveRole?: 'origin' | 'destination' | 'woven';
}

export interface EnergyLinkView {
  id: string;
  sourceNodeId: string;
  destinationNodeId: string;
  sourceCoordinates: readonly [number, number];
  destinationCoordinates: readonly [number, number];
  familyCode: string;
  requiredTier: 1 | 2 | 3 | 4 | 5;
  state: 'dormant' | 'attuned' | 'ready' | 'awakened';
}

export interface KnowledgeWeaveView {
  id: string;
  sourceNodeId: string;
  destinationNodeId: string;
  sourceName: string;
  destinationName: string;
  sourceCoordinates: readonly [number, number];
  destinationCoordinates: readonly [number, number];
  routeCoordinates: readonly (readonly [number, number])[];
  routeKind?: 'direct_preview' | 'public_corridor' | 'local_fixture';
  routeAttribution?: string;
  routeDatasetRelease?: string;
  familyCode: string;
  distanceM: number;
  status: 'planned' | 'awakened' | 'abandoned';
  resonance: 1 | 2 | 3 | 4 | 5;
  plannedAt: string;
  awakenedAt?: string;
  challengeState?: 'journey_required' | 'ready' | 'active' | 'completed' | 'unavailable';
  visitStage?: 'origin_required' | 'destination_required' | 'arrived';
  challengeSessionId?: string;
}

export interface WeaveTargetView {
  nodeId: string;
  name: string;
  nodeKind: NonNullable<DiscoveryMarkerView['nodeKind']>;
  nodeTier: 1 | 2 | 3 | 4 | 5;
  coordinates: readonly [number, number];
  distanceM: number;
  attunementLevel: 0 | 1 | 2 | 3 | 4 | 5;
  sameJourneyVerified: boolean;
  wayfinderImprint: 'verified_visit';
  routeKind?: 'direct_preview' | 'public_corridor' | 'local_fixture';
  state: 'available' | 'attuned' | 'ready' | 'woven';
  existingWeaveId?: string;
}

export interface WorldRouteStopView {
  sequence: number;
  nodeId: string;
  name: string;
  nodeKind: NonNullable<DiscoveryMarkerView['nodeKind']>;
  nodeTier: 1 | 2 | 3 | 4 | 5;
  coordinates: readonly [number, number];
  state: 'visited' | 'next' | 'locked';
}

export interface WorldRouteContractView {
  id: string;
  title: string;
  familyCode: string;
  distanceM: number;
  requiredTier: 1 | 2 | 3 | 4 | 5;
  state: 'available' | 'active' | 'completed';
  currentStep: number;
  stops: readonly WorldRouteStopView[];
  nextNodeId?: string;
}

export interface ExploreViewModel {
  regionName: string;
  center: readonly [number, number];
  discoveries: readonly DiscoveryMarkerView[];
  energyLinks?: readonly EnergyLinkView[];
  weaves?: readonly KnowledgeWeaveView[];
  routeContracts?: readonly WorldRouteContractView[];
  interactionRadiusM: number;
  maxInteractionAccuracyM: number;
  uniqueCells: number;
  journeyActive: boolean;
  journeyId?: string;
}

export interface DecodeSegmentView {
  id: string;
  ringIndex: number;
  angularSlot: number;
  rotation: number;
  /** Present only in the explicit local demo fixture; never sent by the game API. */
  targetRotation?: number;
  locked: boolean;
  channelShape: 'straight' | 'corner' | 'fork';
  channelTypes?: Partial<
    Record<'inner' | 'clockwise' | 'outer' | 'counter_clockwise', 'jade' | 'gold'>
  >;
}

export interface DecodeMoveView {
  moveType?: 'rotate_segment' | 'rotate_ring';
  segmentId?: string;
  ringIndex?: number;
  direction: 'clockwise' | 'counter_clockwise';
  clientElapsedMs: number;
}

export interface DecodeTutorialStepView {
  sequence: number;
  segmentId: string;
  direction: 'clockwise' | 'counter_clockwise';
  concept: 'wake_source' | 'carry_energy' | 'reach_seal';
}

export interface DecodePuzzleDefinition {
  id: string;
  definitionVersion?: number;
  seed: string;
  familyId: string;
  familyLabel: string;
  ringCount: number;
  sectorsPerRing: readonly number[];
  segments: readonly DecodeSegmentView[];
  terminalSegmentIds: readonly string[];
  sourceSegmentIds?: readonly string[];
  rotatableRingIndices?: readonly number[];
  /** A server-authored teaching sequence present only for the non-competitive tutorial. */
  tutorialSteps?: readonly DecodeTutorialStepView[];
  moveLimit?: number;
  parMoves?: number;
  minimumSolutionMoves?: number;
  difficultyEstimate?: number;
  reviewStatus: CulturalReviewStatus;
}

export interface DecodeRunView {
  runId: string;
  sourceDiscoveryId?: string;
  sourceRemoteExpeditionId?: string;
  sourceWeaveId?: string;
  sourceCivicExpeditionId?: string;
  sourcePersonalEncounterId?: string;
  rewardMode?: 'standard' | 'weave_only' | 'civic_only' | 'personal_materials';
  weaveAwakened?: boolean;
  status: 'ready' | 'active' | 'awaiting_verification' | 'solved';
  currentMoves: number;
  hintCount: number;
  profileCode: 'tutorial' | 'common' | 'advanced' | 'mastery';
  modifiers: readonly string[];
  masteryTargets: readonly string[];
  suggestedMove?: Pick<DecodeMoveView, 'direction'> & {
    segmentId: string;
    completesPuzzle: boolean;
  };
  puzzle: DecodePuzzleDefinition;
  rewardPreview: readonly ResourceDisplay[];
}

export interface DecodeProfileView {
  code: 'tutorial' | 'common' | 'advanced' | 'mastery';
  version: number;
  ringCount: number;
  sectorsPerRing: readonly number[];
  modifiers: readonly string[];
  masteryTargets: readonly string[];
  minimumSolutionMoves: number;
}

export interface DecodeMasteryView {
  familyCode: string;
  profileCode: string;
  attempts: number;
  completions: number;
  noHintCompletions: number;
  bestScore: number;
  masteryPoints: number;
  masteryTier: string;
}

export interface CodexEntryView {
  id: string;
  familyId: string;
  status: 'unknown' | 'hinted' | 'found' | 'decoded' | 'mastered';
  title?: string;
  iconAssetKey: string;
  lorePreview?: string;
  masteryProgress?: number;
  foundAt?: string;
  reviewStatus: CulturalReviewStatus;
}

export interface CodexFamilyView {
  id: string;
  label: string;
  motif: string;
  decodedCount: number;
  totalCount: number;
  masteryTier: 'Awakening' | 'Radiance' | 'Ascension' | 'Legend';
  entries: readonly CodexEntryView[];
}

export interface SoloPathView {
  id: 'explorer' | 'artisan' | 'merchant';
  label: string;
  description: string;
  progress: number;
  nextMilestone: string;
}

export interface SoloRankView {
  code: string;
  displayName: string;
  ordinal: number;
  eligible: boolean;
  requirements: Readonly<Record<string, number>>;
  progress: Readonly<Record<string, number>>;
}

export interface SoloContractView {
  id: string;
  code: string;
  specialization: string;
  event: string;
  current: number;
  target: number;
  status: string;
  rewards: readonly ResourceDisplay[];
}

export interface MerchantOrderView {
  id: string;
  code: string;
  specialization: string;
  resourceCode: ResourceCode;
  unitCost: number;
  fulfilledQuantity: number;
  targetQuantity: number;
  status: string;
  completionRewards: readonly ResourceDisplay[];
}

export interface SoloProgressionView {
  independentOfTribe: boolean;
  currentRank: string;
  ranks: readonly SoloRankView[];
  contracts: readonly SoloContractView[];
  merchantOrders: readonly MerchantOrderView[];
}

export type TribeRole = 'member' | 'veteran' | 'officer' | 'leader';

export interface TribeMemberView {
  memberId: string;
  displayName: string;
  role: TribeRole;
  contributionScore: number;
  joinedAt: string;
}

export interface TribeProjectRequirementView {
  resourceCode: ResourceCode;
  label: string;
  current: number;
  target: number;
}

export interface TribeProjectView {
  id: string;
  title: string;
  phase: number;
  phaseCount: number;
  progress: number;
  state: string;
  remainingSeconds: number;
  visualStageAssetKey: string;
  requirements: readonly TribeProjectRequirementView[];
}

export interface TribeSummaryView {
  id: string;
  name: string;
  slug: string;
  leaderMemberId?: string;
  reputation: number;
  rankTier: string;
  memberCount: number;
  currentUserRole?: TribeRole;
  project?: TribeProjectView;
  members: readonly TribeMemberView[];
}

export interface TribeFeedEventView {
  id: string;
  eventType:
    | 'member_joined'
    | 'member_left'
    | 'role_changed'
    | 'project_contributed'
    | 'project_phase_completed'
    | 'rank_promotion'
    | 'ritual_started'
    | 'market_trade_completed'
    | 'civic_expedition_started'
    | 'civic_expedition_contributed'
    | 'civic_expedition_completed';
  actorMemberId?: string;
  payload: Readonly<Record<string, string | number | boolean>>;
  createdAt: string;
}

export interface LeaderboardEntryView {
  rank: number;
  entityId: string;
  displayName: string;
  score: number;
}

export interface TribeSurfaceView {
  discovered: readonly TribeSummaryView[];
  current?: TribeSummaryView;
  feed: readonly TribeFeedEventView[];
  leaderboard: readonly LeaderboardEntryView[];
  leaderboardPeriod?: string;
}

export interface MissionCardView {
  id: string;
  code: string;
  scope: string;
  event: string;
  current: number;
  target: number;
  status: string;
  periodKey: string;
}

export interface PlayerVerticalSliceView {
  schemaVersion: string;
  source: 'demo_fixture' | 'api';
  profile: {
    userId?: string;
    displayName: string;
    level: number;
    locale: string;
    avatarAssetId: string;
    avatarAssetUrl: string;
    avatarLabel: string;
    onboardingStep: number;
    onboardingCompleted: boolean;
  };
  settlement: SettlementViewModel;
  explore: ExploreViewModel;
  decode: DecodeRunView;
  decodeProfiles: readonly DecodeProfileView[];
  decodeMastery: readonly DecodeMasteryView[];
  codexFamilies: readonly CodexFamilyView[];
  soloPaths: readonly SoloPathView[];
  solo: SoloProgressionView;
  missions: readonly MissionCardView[];
  tribe: TribeSurfaceView;
  featureFlags: {
    exploreEnabled: boolean;
    tribesEnabled: boolean;
    paymentsEnabled: boolean;
    rewardExternalizationEnabled: false;
  };
}

export const primaryNavigation: readonly PrimaryNavigationItem[] = [
  { id: 'home', label: 'Home', href: '/home' },
  { id: 'explore', label: 'Explore', href: '/explore' },
  { id: 'decode', label: 'Decode', href: '/decode' },
  { id: 'codex', label: 'Codex', href: '/codex' },
  { id: 'tribe', label: 'Tribe', href: '/tribe' },
] as const;
